/**
 * AnimatedScene — renders one tutorial step as a moving mock screen.
 * Scene specs come from the backend curriculum; nothing here is a screenshot,
 * so the animation never goes stale when the real UI changes.
 */
import { motion } from "framer-motion";
import { MousePointer2, CheckCircle2, AlertTriangle, FileText, UploadCloud, Image as ImageIcon } from "lucide-react";

const rise = (i = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.12 * i + 0.1, duration: 0.45, ease: "easeOut" },
});

// A believable Sterile Mate screen: browser bar, the real sidebar shape, a
// toolbar, then the step's content sliding into the work area.
const NAV = ["Dashboard", "Work Orders", "Sourcing Desk", "Bid Desk", "Orders", "Projects", "Payables"];

const Chrome = ({ title, badge, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 18, scale: 0.985 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.45, ease: "easeOut" }}
    className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-xl shadow-slate-300/50">
    {/* browser bar */}
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
      <span className="ml-3 flex-1 max-w-sm truncate rounded-md bg-white border border-slate-200 px-2 py-0.5 text-[10px] text-slate-400">
        sterilemate.com/admin
      </span>
      {badge && (
        <span className="ml-auto text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 ring-1 ring-sky-200">
          {badge}
        </span>
      )}
    </div>
    <div className="flex">
      {/* sidebar */}
      <div className="hidden sm:block w-[122px] shrink-0 bg-[#0b1f33] py-3 px-2">
        <p className="px-1.5 pb-2 text-[9px] font-black tracking-[0.16em] text-sky-200">ASKISAAC</p>
        {NAV.map((n, i) => (
          <motion.p key={n}
            initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.3 }}
            className={`px-1.5 py-[5px] rounded text-[9.5px] font-semibold truncate ${
              i === 2 ? "bg-sky-500/25 text-white" : "text-slate-300"}`}>
            {n}
          </motion.p>
        ))}
      </div>
      {/* work area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white">
          <p className="text-[12px] font-bold text-slate-900 truncate">{title}</p>
          <span className="ml-auto hidden sm:flex items-center gap-1.5">
            <span className="h-5 w-16 rounded bg-slate-100" />
            <span className="h-5 w-12 rounded bg-sky-600/90" />
          </span>
        </div>
        <div className="relative p-4 bg-[#fbfdff]">
          {/* a single sweep, like a screen that just finished loading */}
          <motion.div
            initial={{ x: "-120%" }} animate={{ x: "120%" }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-sky-100/70 to-transparent" />
          {children}
        </div>
      </div>
    </div>
  </motion.div>
);

const Cursor = ({ row = 0 }) => (
  <motion.div className="absolute left-6 text-sky-600 drop-shadow"
    initial={{ opacity: 0, top: 8 }}
    animate={{ opacity: [0, 1, 1, 1], top: 34 + row * 42 }}
    transition={{ delay: 0.7, duration: 0.9, ease: "easeInOut" }}>
    <MousePointer2 className="w-4 h-4" />
    <motion.span className="absolute -left-2 -top-2 w-8 h-8 rounded-full bg-sky-400/30"
      initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.4], opacity: [0.7, 0] }}
      transition={{ delay: 1.6, duration: 0.7 }} />
  </motion.div>
);

export default function AnimatedScene({ scene, sceneKey }) {
  if (!scene) return null;
  const { type } = scene;

  if (type === "list") {
    return (
      <Chrome title={scene.title} badge={scene.badge}>
        <div className="relative">
          {typeof scene.cursor_to === "number" && <Cursor row={scene.cursor_to} />}
          <div className="space-y-2 pl-6">
            {(scene.rows || []).map((r, i) => (
              <motion.div key={`${sceneKey}-${i}`} {...rise(i)}
                className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 border ${
                  scene.cursor_to === i
                    ? "border-sky-400 bg-sky-50"
                    : "border-slate-200 bg-slate-50"}`}>
                <span className="text-[12.5px] font-semibold text-slate-900">{r.left}</span>
                <span className="text-[11.5px] font-mono text-sky-600">{r.right}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </Chrome>
    );
  }

  if (type === "table") {
    return (
      <Chrome title={scene.title}>
        <table className="w-full text-left">
          <thead>
            <tr>{(scene.head || []).map((h) => (
              <th key={h} className="pb-1.5 text-[9px] font-black uppercase tracking-wider text-slate-600">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(scene.rows || []).map((row, i) => (
              <motion.tr key={`${sceneKey}-${i}`} {...rise(i)}
                className={scene.highlight === i ? "bg-amber-400/10" : ""}>
                {row.map((cell, j) => (
                  <td key={j} className={`py-1.5 text-[12px] ${j === 0
                    ? "font-bold text-slate-900" : "text-slate-700 font-mono"}`}>{cell}</td>
                ))}
              </motion.tr>
            ))}
          </tbody>
        </table>
        {scene.footer && (
          <motion.p {...rise((scene.rows || []).length)}
            className="mt-3 text-[11px] font-black uppercase tracking-wider text-red-300 bg-red-500/10 ring-1 ring-red-500/40 rounded px-2 py-1 inline-block">
            {scene.footer}
          </motion.p>
        )}
      </Chrome>
    );
  }

  if (type === "form") {
    return (
      <Chrome title={scene.title}>
        <div className="space-y-2.5">
          {(scene.fields || []).map(([label, value], i) => (
            <motion.div key={`${sceneKey}-${i}`} {...rise(i)}>
              <p className="text-[9px] font-black uppercase tracking-wider text-slate-600">{label}</p>
              <div className={`mt-0.5 rounded-md px-3 py-2 border ${scene.highlight === i
                ? "border-sky-500/70 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
                <motion.span className="text-[12.5px] font-mono text-slate-900"
                  initial={{ width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }}
                  transition={{ delay: 0.25 + i * 0.2, duration: 0.5 }}>
                  {value}
                </motion.span>
              </div>
            </motion.div>
          ))}
        </div>
      </Chrome>
    );
  }

  if (type === "chips") {
    return (
      <Chrome title={scene.title}>
        <div className="flex flex-wrap gap-2">
          {(scene.chips || []).map((c, i) => (
            <motion.span key={`${sceneKey}-${i}`}
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.12 * i + 0.15, type: "spring", stiffness: 240, damping: 18 }}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold text-emerald-200 bg-emerald-500/15 ring-1 ring-emerald-500/40">
              <CheckCircle2 className="w-3.5 h-3.5" /> {c}
            </motion.span>
          ))}
        </div>
      </Chrome>
    );
  }

  if (type === "flags") {
    const TONE = {
      green: { box: "bg-emerald-500/10 ring-emerald-500/40", ico: "text-emerald-300", lbl: "text-emerald-300" },
      yellow: { box: "bg-amber-500/10 ring-amber-500/40", ico: "text-amber-300", lbl: "text-amber-300" },
      red: { box: "bg-red-500/10 ring-red-500/40", ico: "text-red-300", lbl: "text-red-300" },
    };
    return (
      <Chrome title={scene.title}>
        <div className="space-y-2">
          {(scene.flags || []).map(([code, text], i) => {
            const c = TONE[code] || TONE.yellow;
            return (
              <motion.div key={`${sceneKey}-${i}`} {...rise(i)}
                className={`flex items-start gap-2 rounded-lg px-3 py-2 ring-1 ${c.box}`}>
                <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${c.ico}`} />
                <div>
                  <p className={`text-[9px] font-black uppercase tracking-wider ${c.lbl}`}>{code}</p>
                  <p className="text-[12.5px] text-slate-900">{text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Chrome>
    );
  }

  if (type === "upload") {
    return (
      <Chrome title={scene.title}>
        <motion.div className="rounded-lg border-2 border-dashed border-sky-500/50 bg-sky-500/5 p-4 text-center"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
          <motion.div animate={{ y: [6, -4, 0] }} transition={{ duration: 1.1, repeat: Infinity, repeatDelay: 1.6 }}>
            <UploadCloud className="w-7 h-7 mx-auto text-sky-600" />
          </motion.div>
          <p className="text-[11px] text-sky-200 mt-1">Drop the document — Ask Isaac reads it</p>
        </motion.div>
        <div className="mt-3 space-y-1.5">
          {(scene.lines || []).map((l, i) => (
            <motion.p key={`${sceneKey}-${i}`} {...rise(i + 1)}
              className="text-[12px] font-mono text-emerald-200">· {l}</motion.p>
          ))}
        </div>
      </Chrome>
    );
  }

  if (type === "gallery") {
    return (
      <Chrome title={scene.title}>
        <div className="grid grid-cols-3 gap-2">
          {(scene.tiles || []).map((t, i) => (
            <motion.div key={`${sceneKey}-${i}`} {...rise(i)}
              className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
              <div className="h-16 bg-gradient-to-br from-slate-600/60 to-slate-800 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-slate-600" />
              </div>
              <p className="text-[10.5px] text-slate-800 px-2 py-1.5 leading-tight">{t}</p>
            </motion.div>
          ))}
        </div>
      </Chrome>
    );
  }

  if (type === "gantt") {
    return (
      <Chrome title={scene.title}>
        <div className="space-y-2">
          {(scene.bars || []).map(([label, start, end], i) => (
            <div key={`${sceneKey}-${i}`} className="flex items-center gap-2">
              <span className="w-28 text-[11px] text-slate-700 truncate">{label}</span>
              <div className="flex-1 h-4 rounded bg-slate-100 relative overflow-hidden">
                <motion.div className="absolute top-0 h-4 rounded bg-indigo-400/70"
                  style={{ left: `${(start / 12) * 100}%` }}
                  initial={{ width: 0 }} animate={{ width: `${((end - start) / 12) * 100}%` }}
                  transition={{ delay: 0.15 * i + 0.1, duration: 0.6, ease: "easeOut" }} />
              </div>
            </div>
          ))}
        </div>
      </Chrome>
    );
  }

  if (type === "pdf") {
    return (
      <Chrome title={scene.title}>
        <motion.div className="mx-auto max-w-[280px] rounded-md bg-white p-3 shadow-xl"
          initial={{ opacity: 0, rotate: -2, y: 16 }} animate={{ opacity: 1, rotate: 0, y: 0 }}
          transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-900" />
            <span className="text-[9px] font-black uppercase tracking-wider text-slate-900">Sterile Mate</span>
          </div>
          {(scene.lines || []).map((l, i) => (
            <motion.div key={`${sceneKey}-${i}`} {...rise(i + 1)} className="mt-1.5">
              <p className="text-[9.5px] font-bold text-slate-800">{l}</p>
              <div className="mt-0.5 space-y-0.5">
                <div className="h-1 bg-slate-200 rounded w-full" />
                <div className="h-1 bg-slate-200 rounded w-4/5" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Chrome>
    );
  }

  return null;
}
